import React, { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { css } from '@emotion/react'
import Link from 'redux-first-router-link'

import Dropdown from '@groovehq/internal-design-system/lib/components/Dropdown/Dropdown'
import Button from '@groovehq/internal-design-system/lib/components/Button/Button'
import Tooltip from '@groovehq/internal-design-system/lib/components/Tooltip/Tooltip'
import { styles as menuStyles } from '@groovehq/internal-design-system/lib/components/Menu/Menu.styles'
import SettingsIcon from '@groovehq/internal-design-system/lib/assets/icons/Settings'
import { divider } from '@groovehq/internal-design-system/lib/styles/elements'

import { useDrawer } from 'ducks/drawers/hooks'
import {
  DRAWER_TYPE_AGENT_ADD,
  DRAWER_TYPE_USERS_OVERVIEW,
  DRAWER_TYPE_TEAMS_OVERVIEW,
} from 'ducks/drawers/types'
import { selectSettingsLandingPageAction } from 'selectors/menus'
import {
  SETTINGS_WIDGET_CREATE_NEW_PAGE,
  SETTINGS_WIDGETS_PAGE,
  SETTINGS_PREFERENCES_PAGE,
} from 'subapps/settings/types'
import { doShowChatAvailabilityModal } from 'actions/modals'
import {
  selectCurrentUserIsAdmin,
  selectCurrentUserIsOwner,
} from 'ducks/currentUser'

const menuStyle = () => theme => css`
  & li,
  & li a {
    color: ${theme.color.monochrome.black} !important;
  }
`

const FullMenu = React.memo(() => {
  const { Menu } = Dropdown

  const dispatch = useDispatch()
  const isOwner = useSelector(selectCurrentUserIsOwner)
  const isAdmin = useSelector(selectCurrentUserIsAdmin)
  const toSettingsLandingPage = useSelector(selectSettingsLandingPageAction)
  const isManager = isOwner || isAdmin

  const { openDrawer: openAddAgent } = useDrawer({
    type: DRAWER_TYPE_AGENT_ADD,
    lazyLoad: true,
  })

  const onOpenAddAgent = useCallback(
    () => {
      openAddAgent('new', {
        additionalProps: { title: `Add ${app.t('Agent')}` },
      })
    },
    [openAddAgent]
  )

  const onOpenChatAvailability = useCallback(
    () => {
      dispatch(doShowChatAvailabilityModal())
    },
    [dispatch]
  )

  const { openDrawer: openUsersOverview } = useDrawer({
    type: DRAWER_TYPE_USERS_OVERVIEW,
  })

  const { openDrawer: openTeamsOverview } = useDrawer({
    type: DRAWER_TYPE_TEAMS_OVERVIEW,
  })

  return (
    <div css={menuStyle}>
      {isManager && (
        <>
          <Menu>
            <Menu.Item itemKey="add-widget">
              <Link to={{ type: SETTINGS_WIDGET_CREATE_NEW_PAGE }}>
                Add a live chat widget
              </Link>
            </Menu.Item>
            <Menu.Item itemKey="add-agent" onClick={onOpenAddAgent}>
              Invite {app.t('agent_with_article')}
            </Menu.Item>
          </Menu>
          <div css={divider.styles.base} role="separator" />
        </>
      )}
      <Menu>
        <Menu.Item itemKey="chat-header" header>
          Chat Settings
        </Menu.Item>
        <Menu.Item itemKey="visibility" indent onClick={onOpenChatAvailability}>
          Live chat visibility
        </Menu.Item>
        {isManager && (
          <Menu.Item itemKey="widget-settings" indent>
            <Link to={{ type: SETTINGS_WIDGETS_PAGE }}>
              Live chat &amp; widget settings
            </Link>
          </Menu.Item>
        )}
        <Menu.Item itemKey="inbox-prefs" indent>
          <Link to={{ type: SETTINGS_PREFERENCES_PAGE }}>
            {app.t('Mailbox')} preferences
          </Link>
        </Menu.Item>
        {isManager && (
          <Menu.Item itemKey="users" indent onClick={openUsersOverview}>
            {app.t('Agents')}
          </Menu.Item>
        )}
        {isManager && (
          <Menu.Item itemKey="team" indent onClick={openTeamsOverview}>
            {app.t('Groups')}
          </Menu.Item>
        )}
      </Menu>
      <div css={divider.styles.base} role="separator" />
      <Menu>
        <Menu.Item itemKey="more">
          <Link to={toSettingsLandingPage}>More settings&hellip;</Link>
        </Menu.Item>
      </Menu>
    </div>
  )
})

const SettingsMenu = React.memo(() => {
  return (
    <Dropdown
      overlay={<FullMenu />}
      hasMinWidth
      autoHeight
      strategy="fixed"
      position="bottom-start"
      isNavByArrowsDisabled
    >
      {/* deliberately have a div wrapper here because PopperJS throws an error if Tooltip is the immediate child */}
      <div>
        <Tooltip position="bottom" title="Chat Settings">
          <Button
            type="icon"
            size="small"
            customIcon={<SettingsIcon />}
            css={menuStyles.controlIconButton}
          />
        </Tooltip>
      </div>
    </Dropdown>
  )
})

export default SettingsMenu
